import React from 'react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiSortAscending, PiSortDescending } from 'react-icons/pi';

export type SortDirection = 'asc' | 'desc';

interface SortButtonProps {
  /**
   * Current sort direction
   */
  direction: SortDirection;
  /**
   * Label to display for the current sort type
   */
  label: string;
  /**
   * Whether the button is in a trigger state (e.g., for dropdown menus)
   */
  asChild?: boolean;
  /**
   * Button variant
   */
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link';
  /**
   * Button size
   */
  size?: 'default' | 'sm' | 'lg' | 'icon';
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Click handler
   */
  onClick?: () => void;
  /**
   * Whether to show the label on small screens
   */
  hideLabel?: boolean;
  /**
   * Custom tooltip content
   */
  tooltipContent?: string;
}

export const SortButton = React.forwardRef<HTMLButtonElement, SortButtonProps>(
  (
    {
      direction,
      label,
      asChild = false,
      variant = 'outline',
      size = 'sm',
      className = '',
      onClick,
      hideLabel = false,
      tooltipContent,
      ...props
    },
    ref,
  ) => {
    const DirectionIcon =
      direction === 'asc' ? PiSortAscending : PiSortDescending;
    const defaultTooltip = `Sort by ${label.toLowerCase()} (${
      direction === 'asc' ? 'ascending' : 'descending'
    })`;

    const buttonContent = (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={`h-10 w-24 justify-start ${className}`}
        onClick={onClick}
        {...props}
      >
        <DirectionIcon className="size-4 flex-shrink-0" />
        <span
          className={`truncate ml-2 ${
            hideLabel ? 'hidden sm:inline-block' : ''
          }`}
        >
          {label}
        </span>
      </Button>
    );

    if (asChild) {
      return buttonContent;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
        <TooltipContent>{tooltipContent || defaultTooltip}</TooltipContent>
      </Tooltip>
    );
  },
);

SortButton.displayName = 'SortButton';
